import { Box, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DataGrid,
  DataGridProps,
  GridFilterModel,
  GridPaginationModel,
  GridSortDirection,
  GridSortModel,
  GridToolbar,
  GridValidRowModel,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useLayoutEffect, useRef, useState } from "react";

interface DataGridComponent {
  <R extends GridValidRowModel = any>(
    props: DataGridProps<R> & React.RefAttributes<HTMLDivElement>,
  ): React.JSX.Element;
  propTypes?: any;
}

// The grid's own height follows its rows, so with none yet the body is 0px tall
// and a loading overlay has nowhere to draw -- the table just sits empty until
// the response lands. While loading an empty table, hold the body open far
// enough for the spinner to read.
const EMPTY_LOADING_BODY_HEIGHT = 200;
// The spinner sits in the middle of whatever part of the table is on screen.
// Centred on the table itself it would land off-screen for a reader who paged
// from the footer of a long page -- the rows dim and nothing says why.
const useVisibleCentre = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number>();
  useLayoutEffect(() => {
    const place = () => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return;
      const visibleTop = Math.max(box.top, 0);
      const visibleBottom = Math.min(box.bottom, window.innerHeight);
      const centre = visibleBottom > visibleTop ? (visibleTop + visibleBottom) / 2 : box.top + box.height / 2;
      const next = centre - box.top;
      setTop((prev) => (prev === next ? prev : next));
    };
    place();
    // The grid sizes the overlay from its own observer, after this has run,
    // and with no window event to say so.
    const resize = new ResizeObserver(place);
    if (ref.current) resize.observe(ref.current);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      resize.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, []);
  return { ref, top };
};

const SpinnerOverlay = () => {
  const { ref, top } = useVisibleCentre();
  return (
    <Box
      ref={ref}
      role="progressbar"
      aria-label="Loading"
      sx={{
        position: "relative",
        height: "100%",
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
      }}
    >
      {/* On a disc of its own so it reads over rows of text, not through them. */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: top ?? "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          padding: 1.5,
          borderRadius: "50%",
          backgroundColor: "background.paper",
          boxShadow: 3,
        }}
      >
        <CircularProgress size={36} thickness={4.5} />
      </Box>
    </Box>
  );
};

export const ServerDataGrid: DataGridComponent = ({ slots, sx, ...props }) => {
  const emptyWhileLoading = props.loading && props.rows.length === 0;
  return (
    <DataGrid
      // A caller's `slots` replaces the default toolbar, as it always has, but
      // never drops the spinner: every server table shows the same one.
      slots={{ ...(slots ?? { toolbar: GridToolbar }), loadingOverlay: slots?.loadingOverlay ?? SpinnerOverlay }}
      slotProps={{
        ...props.slotProps,
        toolbar: {
          showQuickFilter: true,
          quickFilterProps: { sx: { width: 200 } },
        },
      }}
      paginationMode="server"
      sortingMode="server"
      filterMode="server"
      hideFooterSelectedRowCount
      pageSizeOptions={[10, 25, 50, 100]}
      sx={[
        emptyWhileLoading && { "& .MuiDataGrid-main": { minHeight: EMPTY_LOADING_BODY_HEIGHT } },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...props}
    />
  );
};

export const useSorting = (field: string, sort: GridSortDirection = "asc") =>
  useState<GridSortModel>([
    {
      field,
      sort,
    },
  ]);

export const usePagination = (pageSize = 10, page = 0) =>
  useState<GridPaginationModel>({
    pageSize,
    page,
  });

export const useGridFilters = (filters?: GridFilterModel) =>
  useState<GridFilterModel | undefined>(filters);

export const currencyFormatter = (params: any) => {
  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(params.value);

  return <span>{formattedValue}</span>;
};

export const currencyFormatterRedGreen = (params: any) => {
  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(params.value);

  return <span style={{color: params.value < 0 ? "red" : "green"}}>{formattedValue}</span>;
};

export const qtyFormatter = (params: any) => {
  const formattedValue = parseFloat(params.value || 0).toFixed(2);

  return <span>{formattedValue}</span>;
};

export const qtyFormatterWhole = (params: any) => {
  const formattedValue = parseFloat(params.value || 0).toFixed(0);

  return <span>{formattedValue}</span>;
};

export const availableQtyFormatter = (params: any) => {
  const { value } = params;
  let formattedValue = 'all';

  if (value !== null) {
    formattedValue = parseFloat(value).toFixed(0);
  }

  return <span>{formattedValue}</span>;
}

export const dateFormatter = (params: any) => {
  const formattedValue = dayjs(params.value).format("MM/DD/YYYY");

  return <span>{formattedValue}</span>;
}